import { useQuery } from "@apollo/client/react";
import styled from "@emotion/styled";
import { List, ListGroup, ListItem, Navbar, Page } from "framework7-react";
import { useEffect, useState } from "react";

import { DailyDiaryDateTitle } from "@/components/DailyDiaryDateTitle";
import { NavLeftEmpty } from "@/components/NavLeftEmpty";
import { NavRightEmpty } from "@/components/NavRightEmpty";
import { NavTitle } from "@/components/NavTitle";
import { differenceInDays, formatISODateString, formatMonthWithYear, prevMonth, startOfMonth } from "@/config/date-fns";
import { createPage, PageComponentProps } from "@/factories/createPage";
import { getFragmentData } from "@/generated/graphql";
import { DailyShortDiaryFragmentDoc, GetDailyDiariesDocument } from "@/generated/graphql/graphql";
import { usePageReady } from "@/hooks/usePageReady";

const MAX_MONTH_LENGTH = 12;

const ListItemLayout = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  overflow: hidden;

  div {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const DailyDiaryListPage = createPage("DailyDiaryListPage", PageComponent);

function PageComponent({ pageName }: PageComponentProps) {
  const { isPageReady, getPageProps } = usePageReady(pageName, { isTabPage: true });
  const [months, setMonths] = useState<Date[]>([]);

  useEffect(() => {
    if (isPageReady) {
      const today = new Date();
      const currentMonth = startOfMonth(today);
      const isFirstHalfOfMonth = differenceInDays(currentMonth, today) <= 15;
      setMonths(isFirstHalfOfMonth ? [currentMonth, prevMonth(currentMonth)] : [currentMonth]);
    }
  }, [isPageReady]);

  return (
    <Page
      data-component={pageName}
      infinite={months.length < MAX_MONTH_LENGTH}
      infiniteDistance={50}
      onInfinite={() =>
        setMonths((currentMonths) => {
          const pastMonth = currentMonths[currentMonths.length - 1];
          return [...currentMonths, prevMonth(pastMonth)];
        })
      }
      {...getPageProps()}
    >
      <Navbar>
        <NavLeftEmpty />
        <NavTitle>日記</NavTitle>
        <NavRightEmpty />
      </Navbar>
      <List
        dividers
        className="no-margin-top"
      >
        {months.map((month) => (
          <DailyDiaryListGroup
            key={formatISODateString(month)}
            isPageReady={isPageReady}
            month={month}
          />
        ))}
      </List>
    </Page>
  );
}

function DailyDiaryListGroup({ isPageReady, month }: { isPageReady: boolean; month: Date }) {
  const { data } = useQuery(GetDailyDiariesDocument, {
    skip: !isPageReady,
    variables: {
      month: formatISODateString(month),
    },
  });
  const dailyDiaries = getFragmentData(DailyShortDiaryFragmentDoc, data?.dailyDiaries || []);

  return (
    <ListGroup>
      <ListItem
        groupTitle
        title={formatMonthWithYear(month)}
      />
      {dailyDiaries.map((dailyDiary) => (
        <ListItem
          key={dailyDiary.id}
          noChevron
          link={`/diary/daily/${formatISODateString(dailyDiary.date)}/`}
        >
          <ListItemLayout>
            <DailyDiaryDateTitle dailyDiary={dailyDiary} />
            <div>{dailyDiary.shortBody}</div>
          </ListItemLayout>
        </ListItem>
      ))}
    </ListGroup>
  );
}
